import React from 'react';
import { AgeChip } from './AgeChip.jsx';

/* The most-used component in the product. Label above value, never beside.
   NO border, NO background — tiles sit directly on the surface separated by 24px
   gaps, because boxing each one turns a four-number summary into four things to
   read instead of one. */

const SIZE = { hero: 'display', default: 'lg', inline: 'md' };

export function MetricTile({ label, value, context, age, ageState = 'fresh', variant = 'default', translation, style, children }) {
  const step = SIZE[variant] || 'lg';
  const stale = ageState === 'stale';
  const sizeVar = {
    display: ['var(--num-display-size)', 'var(--num-display-line)', 'var(--num-display-weight)', 'var(--num-display-tracking)'],
    lg: ['var(--num-lg-size)', 'var(--num-lg-line)', 'var(--num-lg-weight)', 'var(--num-lg-tracking)'],
    md: ['var(--num-md-size)', 'var(--num-md-line)', 'var(--num-md-weight)', 'var(--num-md-tracking)'],
  }[step];

  // `data-variant` is a styling hook for the container, not decoration: a hero tile sets
  // its value at --num-display (48px), which does not fit the same grid track as a 22px
  // one. `.jmt-tiles` gives it two. Without it, `€48,120.44` overran the tile beside it.
  return (
    <div data-variant={variant} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-label-value)', minWidth: 0, ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-8)' }}>
        <span style={{
          fontSize: 'var(--label-size)', lineHeight: 'var(--label-line)', fontWeight: 'var(--label-weight)',
          letterSpacing: 'var(--label-tracking)', textTransform: 'uppercase', color: 'var(--text-tertiary)',
        }}>{label}</span>
        {age ? <span style={{ marginLeft: 'auto' }}><AgeChip age={age} state={ageState} /></span> : null}
      </div>

      {/* The value has NO transition, deliberately, and this is the load-bearing line
          in the component. `--dur-staleness` is `0ms`: the instant data stops being
          trustworthy the figure must render in --text-disabled on that same frame. A
          200ms colour ramp here is 200ms of a stale number still looking current, and
          that is the most expensive defect this interface can have. Same reasoning
          forbids a count-up or a crossfade on the value changing — see
          `tokens/motion.css`, the carve-outs. */}
      <div style={{
        fontFamily: 'var(--font-ui)', fontVariantNumeric: 'tabular-nums slashed-zero',
        fontSize: sizeVar[0], lineHeight: sizeVar[1], fontWeight: sizeVar[2],
        letterSpacing: sizeVar[3],
        color: stale ? 'var(--text-disabled)' : 'var(--text-primary)',
        transition: 'none',
      }}>{children != null ? children : (value == null || value === '' ? '—' : value)}</div>

      {context ? (
        <div style={{
          fontSize: 'var(--caption-size)', lineHeight: 'var(--caption-line)', color: 'var(--text-secondary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{context}</div>
      ) : null}

      {translation ? (
        <div style={{
          fontSize: 'var(--caption-size)', lineHeight: 'var(--caption-line)', color: 'var(--text-tertiary)',
          maxWidth: '38ch',
        }}>{translation}</div>
      ) : null}
    </div>
  );
}
